import { useContext, useEffect, useState } from "react";
import { AuthContext } from "context";
import EditUniqueNumDialog from "./edit-unique-number";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { Grid } from "@mui/material";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import FormField from "layouts/applications/wizard/components/FormField";
import IconButton from "@mui/material/IconButton";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import Tooltip from "@mui/material/Tooltip";
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from "@mui/icons-material/Remove";


export default function GeneralInfoFields({
    changeHandler,
    part,
    error
}) {
    const [userRole, setUserRole] = useState("");
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [radioValue, setRadioValue] = useState("1");
    const [manufacturerCount, setManufacturerCount] = useState(1);
    const [mpnList, setMpnList] = useState([]);

    // Get user role from context
    const authContext = useContext(AuthContext);
    useEffect(() => {
        (async () => {
            const role = await authContext.getRole();
            setUserRole(role);
        })();
    }, [])

    useEffect(() => {
        let newMpnList = [];
        // Handle both array and object structures for backward compatibility
        if (part.mpn) {
            if (Array.isArray(part.mpn)) {
                newMpnList = [...part.mpn];
            } else if (typeof part.mpn === 'object') {
                newMpnList = Object.values(part.mpn);
            }
        }

        // Ensure there is at least one field
        if (newMpnList.length === 0) {
            newMpnList = [""];
        }

        setMpnList(newMpnList);
        setManufacturerCount(newMpnList.length);

        if (!part.preferred_mpn) {
            changeHandler({
                target: {
                    name: "preferred_mpn",
                    value: "0"
                }
            });
        }
    }, [part.mpn]);

    // Set the radio button value when part data is loaded
    useEffect(() => {
        console.log("Preferred Vendor:", part.preferred_vendor);
        setRadioValue(part.preferred_vendor);
    }, [part.preferred_vendor]);

    const handleAddManufacturer = () => {
        const updatedList = [...mpnList, ""];
        setMpnList(updatedList);
        setManufacturerCount(updatedList.length);
        changeHandler({ target: { name: "mpn", value: updatedList } });
    }

    const handleRemoveManufacturer = (index) => {
        const updatedList = mpnList.filter((_, i) => i !== index);
        setMpnList(updatedList);
        setManufacturerCount(updatedList.length);
        changeHandler({ target: { name: "mpn", value: updatedList } });

        const currentPreferred = parseInt(part.preferred_mpn || "0", 10);

        if (index < currentPreferred) {
            changeHandler({
                target: {
                    name: "preferred_mpn",
                    value: (currentPreferred - 1).toString()
                }
            });
        } else if (index === currentPreferred) {
            const newPreferred = index > 0 ? index - 1 : 0;
            changeHandler({
                target: {
                    name: "preferred_mpn",
                    value: newPreferred.toString()
                }
            });
        }
    };

    const handleMpnChange = (index, value) => {
        const updatedList = [...mpnList];
        updatedList[index] = value;
        setMpnList(updatedList);
        changeHandler({ target: { name: "mpn", value: updatedList } });
    };

    // Open the edit unique number dialog
    const handleOpenEditDialog = () => setOpenEditDialog(true);

    // Close the edit unique number dialog
    const handleCloseEditDialog = (unique_num) => {
        setOpenEditDialog(false)
        if (unique_num && !isNaN(unique_num)) {
            changeHandler({ target: { name: "unique_num", value: unique_num } });
        }
    };

    // Radio button change handler
    const radioChangeHandler = (event) => {
        setRadioValue(event.target.value);
        changeHandler({
            target: {
                name: "preferred_vendor",
                value: event.target.value
            }
        });
    }

    return (
        <MDBox>

            <EditUniqueNumDialog open={openEditDialog} handleClose={handleCloseEditDialog} part={part} />

            <MDTypography variant="h5" fontWeight="bold" mt={1} mb={2}>
                General Informations :
            </MDTypography>

            <Grid container spacing={2}>
                <Grid item xs={4} sm={4}>
                    {/* Parent Category */}
                    <MDInput
                        error={error.parentCategory}
                        label="Parent Category"
                        value={part.partCategory.parentCategory}
                        name="Parent Category"
                        fullWidth
                        disabled
                    />
                    {error.parentCategory && (
                        <MDTypography variant="caption" color="error" fontWeight="light">
                            {error.parentCategory}
                        </MDTypography>
                    )}
                </Grid>

                <Grid item xs={4} sm={4}>
                    {/* Category Code */}
                    <MDInput
                        error={error.categoryCode}
                        label="Part Category"
                        value={part.partCategory.categoryCode}
                        name="Part Category"
                        fullWidth
                        disabled
                    />
                    {error.categoryCode && (
                        <MDTypography variant="caption" color="error" fontWeight="light">
                            {error.categoryCode}
                        </MDTypography>
                    )}
                </Grid>
                <Grid item xs={4} sm={4}>
                    {/* Revision Number */}
                    <FormField
                        label="Revision Number"
                        value={part.revision_num}
                        name="revision_num"
                        onChange={changeHandler}
                        inputProps={{ type: "number" }}
                    />
                </Grid>
            </Grid>

            <MDBox mt={2}>
                <MDBox display="flex">
                    <MDInput
                        label="Generated UID"
                        placeholder=" "
                        name="generatedUid"
                        value={part.generatedUID}
                        fullWidth
                        disabled
                        inputProps={{
                            autoComplete: "examples",
                            form: {
                                autoComplete: "off",
                            },
                        }}
                    />
                    {userRole === "admin" &&
                        <Tooltip title="Modify Unique Number" onClick={handleOpenEditDialog}>
                            <IconButton>
                                <EditTwoToneIcon />
                            </IconButton>
                        </Tooltip>}
                </MDBox>
                {/*</Grid>
                    <Grid item xs={1} sm={1}>

                        <Tooltip title="Edit"
                        >
                            <IconButton>
                                <EditIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>*/}
            </MDBox>

            <MDBox
                display="flex"
                flexDirection="column"
                fullWidth
                mt={2}
            >
                {/* Part Description */}
                <MDInput
                    error={error.description}
                    label="Description"
                    placeholder=" "
                    name="description"
                    value={part.description}
                    onChange={changeHandler}
                    fullWidth
                    disabled
                    inputProps={{
                        autoComplete: "description",
                        form: {
                            autoComplete: "off",
                        },
                    }}
                />
                {error.description && (
                    <MDTypography variant="caption" color="error" fontWeight="light">
                        {error.description}
                    </MDTypography>
                )}
            </MDBox>

            <Grid container spacing={3} mt={0}>
                {/* Manufacturer Name*/}
                <Grid item xs={12} sm={12}>
                    <MDInput
                        error={error.manufacturer}
                        label="Manufacturer"
                        placeholder=" "
                        name="manufacturer"
                        value={part.manufacturer}
                        onChange={changeHandler}
                        fullWidth
                        inputProps={{
                            autoComplete: "manufacturer",
                            form: {
                                autoComplete: "off",
                            },
                        }}
                    />
                    {error.manufacturer && (
                        <MDTypography variant="caption" color="error" fontWeight="light">
                            {error.manufacturer}
                        </MDTypography>
                    )}
                </Grid>
                <MDBox mt={1} ml={3}>
                    <MDTypography variant="h6" fontWeight="light">
                        Preferred Manufacturer Part Number :
                    </MDTypography>
                </MDBox>

                {/* Manufacturer Part Numbers*/}
                <Grid item xs={12} sm={12}>
                    <MDBox>
                        <RadioGroup
                            value={part.preferred_mpn || "0"}
                            onChange={(event) => changeHandler({
                                target: {
                                    name: "preferred_mpn",
                                    value: event.target.value
                                }
                            })}
                        >
                            {mpnList.map((mpn, index) => (
                                <Grid container spacing={1} key={index} alignItems="center" sx={{ mb: 2 }}>
                                    <Grid item xs="auto">
                                        <FormControlLabel
                                            value={index.toString()}
                                            control={<Radio />}
                                            label=""
                                        />
                                    </Grid>
                                    <Grid item xs={true} sm={true}>
                                        <MDInput
                                            label={`Manufacturer Part Number ${index + 1}`}
                                            placeholder=" "
                                            name={`mpn.${index}`}
                                            value={mpn}
                                            onChange={(e) => handleMpnChange(index, e.target.value)}
                                            error={error[`mpn.${index}`]}
                                            fullWidth
                                            inputProps={{
                                                autoComplete: "manufacturer_part_number",
                                                form: {
                                                    autoComplete: "off",
                                                },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs="auto">
                                        {index === mpnList.length - 1 ? (
                                            <IconButton
                                                onClick={handleAddManufacturer}
                                                size="small"
                                                color="success"
                                            >
                                                <AddIcon />
                                            </IconButton>
                                        ) : (
                                            <IconButton
                                                onClick={() => handleRemoveManufacturer(index)}
                                                size="small"
                                                color="error"
                                            >
                                                <RemoveIcon />
                                            </IconButton>
                                        )}
                                    </Grid>
                                </Grid>
                            ))}
                        </RadioGroup>
                    </MDBox>
                    {error.manufacturer_part_number && (
                        <MDTypography variant="caption" color="error" fontWeight="light">
                            {error.manufacturer_part_number}
                        </MDTypography>
                    )}
                </Grid>
            </Grid>
            <MDBox mt={1}>
                <MDTypography variant="h9" mt={1}>
                    Vendors Information :
                </MDTypography>
                <MDTypography variant="h6" fontWeight="light">
                    Preferred <br /> Vendor :
                </MDTypography>
            </MDBox>
            {/* Preferred Vendor Selector */}
            <Grid container spacing={1} mt={0.5}>
                <Grid item xs={12} sm={1.5}>
                    <MDBox mt={1} ml={3}>
                        <RadioGroup
                            defaultValue={radioValue}
                            value={radioValue}
                            onChange={radioChangeHandler}
                        >
                            <FormControlLabel value="1" control={<Radio />} label="1" />
                            <FormControlLabel value="2" control={<Radio />} label="2" />

                        </RadioGroup>
                    </MDBox>
                </Grid>
                {/* Vendor Name*/}
                <Grid item xs={12} sm={5.5}>
                    {/* Vendor 1 Name */}
                    <MDInput
                        label="Vendor 1"
                        name="vendors.vendor-1"
                        value={part.vendors && part.vendors["vendor-1"] ? part.vendors["vendor-1"] : ""}
                        onChange={changeHandler}
                        error={error.vendor1}
                        fullWidth
                        inputProps={{
                            autoComplete: "vendor",
                            form: {
                                autoComplete: "off",
                            },
                        }}
                    />
                    {error.vendor1 && (
                        <MDTypography variant="caption" color="error" fontWeight="light">
                            {error.vendor1}
                        </MDTypography>
                    )}
                    <MDBox mt={1}>
                        {/* Vendor 2 Name */}
                        <MDInput
                            label="Vendor 2"
                            placeholder=" "
                            name="vendors.vendor-2"
                            value={part.vendors && part.vendors["vendor-2"] ? part.vendors["vendor-2"] : ""}
                            onChange={changeHandler}
                            error={error.vendor2}
                            fullWidth
                            inputProps={{
                                autoComplete: "vendor",
                                form: {
                                    autoComplete: "off",
                                },
                            }}
                        />
                        {error.vendor2 && (
                            <MDTypography variant="caption" color="error" fontWeight="light">
                                {error.vendor2}
                            </MDTypography>
                        )}
                    </MDBox>
                </Grid>
                {/* Vendor Links*/}
                <Grid item xs={12} sm={5}>
                    {/* Vendor Link 1 */}
                    <MDInput
                        label="Vendor Link 1"
                        placeholder=" "
                        name="vendors.link-1"
                        value={part.vendors && part.vendors["link-1"] ? part.vendors["link-1"] : ""}
                        onChange={changeHandler}
                        error={error.link1}
                        fullWidth
                        inputProps={{
                            autoComplete: "vendor",
                            form: {
                                autoComplete: "off",
                            },
                        }}
                    />
                    {error.link1 && (
                        <MDTypography variant="caption" color="error" fontWeight="light">
                            {error.link1}
                        </MDTypography>
                    )}
                    <MDBox mt={1}>
                        {/* Vendor Link 2 */}
                        <MDInput
                            label="Vendor Link 2"
                            placeholder=" "
                            name="vendors.link-2"
                            value={part.vendors && part.vendors["link-2"] ? part.vendors["link-2"] : ""}
                            onChange={changeHandler}
                            error={error.vendor}
                            fullWidth
                            inputProps={{
                                autoComplete: "vendor",
                                form: {
                                    autoComplete: "off",
                                },
                            }}
                        />
                        {error.link2 && (
                            <MDTypography variant="caption" color="error" fontWeight="light">
                                {error.link2}
                            </MDTypography>
                        )}
                    </MDBox>
                </Grid>
            </Grid>
            <MDBox mt={1}>
                {/* Vendor Part Number */}
                <MDInput
                    label="Vendor Part Number"
                    placeholder=" "
                    name="vendor_part_number"
                    value={part.vendor_part_number}
                    onChange={changeHandler}
                    error={error.vendor_part_number}
                    fullWidth
                    inputProps={{
                        autoComplete: "vendor_part_number",
                        form: {
                            autoComplete: "off",
                        },
                    }}
                />
                {error.vendor_part_number && (
                    <MDTypography variant="caption" color="error" fontWeight="light">
                        {error.vendor_part_number}
                    </MDTypography>
                )}
            </MDBox>
        </MDBox>
    );
}
